import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import AuthService from '../../services/authService';

const PointsPurchaseModal = ({ onClose }) => {
  const [selectedPackage, setSelectedPackage] = useState('medium'); // default to middle option
  
  // Tier emojis matching LeadClaimModal
  const TIER_EMOJI = {
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    platinum: '💎'
  };

  // Light mode permanent (dark mode removed from portal)
  const [isLightMode] = useState(true);

  const packages = {
    small: {
      id: 'small',
      points: 65,
      price: 49,
      pricePerPoint: 0.89,
      description: 'Perfect for getting started',
      stripeLink: 'https://buy.stripe.com/bJeaEY3Cgdu80Dk4na3F602',
      tiers: {
        bronze: 6,    // 10 points each = 50 points total
        silver: 2,    // 25 points each = 50 points total
        gold: 1,      // 45 points each = 45 points total
        platinum: 1   // 55 points each = 55 points total
      }
    },
    medium: {
      id: 'medium',
      points: 135,
      price: 99,
      pricePerPoint: 0.83,
      description: 'Most popular choice',
      badge: 'MOST POPULAR',
      stripeLink: 'https://buy.stripe.com/bJe9AU4Gk89OfyebPC3F603',
      tiers: {
        bronze: 13,   // 10 points each = 120 points
        silver: 5,    // 25 points each = 100 points
        gold: 3,      // 45 points each = 90 points
        platinum: 2   // 55 points each = 110 points
      }
    },
    large: {
      id: 'large',
      points: 290,
      price: 199,
      pricePerPoint: 0.80,
      description: 'Maximum value',
      stripeLink: 'https://buy.stripe.com/cNi00k2ycbm099Q06U3F604',
      tiers: {
        bronze: 29,   // 10 points each = 250 points
        silver: 11,   // 25 points each = 250 points
        gold: 6,      // 45 points each = 225 points
        platinum: 4   // 55 points each = 220 points
      }
    }
  };

  const handleCheckout = () => {
    const selectedPkg = packages[selectedPackage];
    const agent = AuthService.getCurrentUser();
    const agentId = agent?._id;
    
    // Append agent ID so Stripe sends it back in the webhook for Zapier
    const checkoutUrl = agentId 
      ? `${selectedPkg.stripeLink}?client_reference_id=${agentId}`
      : selectedPkg.stripeLink;
    
    window.open(checkoutUrl, '_blank');
  };

  const styles = getStyles(isLightMode);

  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerText}>
            <h2 style={styles.title}>Choose your points package</h2>
            <p style={styles.subtitle}>Add additional points before your next renewal</p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {/* Package Options */}
        <div style={styles.packagesContainer}>
          {Object.entries(packages).map(([key, pkg]) => (
            <div
              key={key}
              style={{
                ...styles.packageCard,
                ...(selectedPackage === key ? styles.packageCardSelected : {})
              }}
              onClick={() => setSelectedPackage(key)}
            >
              {pkg.badge && (
                <div style={styles.badge}>{pkg.badge}</div>
              )}
              
              <div style={styles.packageHeader}>
                <div style={styles.pointsAmount}>{pkg.points}</div>
                <div style={styles.pointsLabel}>Points</div>
              </div>
              
              <div style={styles.price}>${pkg.price}</div>
              
              <div style={styles.description}>{pkg.description}</div>

              {/* Tier Breakdown */}
              <div style={styles.tierBreakdown}>
                <div style={styles.tierItem}>
                  <span>
                    <span style={styles.tierEmoji}>{TIER_EMOJI.bronze}</span>
                    Bronze
                  </span>
                  <span style={styles.tierCount}>{pkg.tiers.bronze || 0}</span>
                </div>
                <div style={styles.tierItem}>
                  <span>
                    <span style={styles.tierEmoji}>{TIER_EMOJI.silver}</span>
                    Silver
                  </span>
                  <span style={styles.tierCount}>{pkg.tiers.silver || 0}</span>
                </div>
                <div style={styles.tierItem}>
                  <span>
                    <span style={styles.tierEmoji}>{TIER_EMOJI.gold}</span>
                    Gold
                  </span>
                  <span style={styles.tierCount}>{pkg.tiers.gold || 0}</span>
                </div>
                <div style={styles.tierItem}>
                  <span>
                    <span style={styles.tierEmoji}>{TIER_EMOJI.platinum}</span>
                    Platinum
                  </span>
                  <span style={styles.tierCount}>{pkg.tiers.platinum || 0}</span>
                </div>
              </div>

              {/* Radio Button */}
              <div style={styles.radioContainer}>
                <div style={{
                  ...styles.radio,
                  ...(selectedPackage === key ? styles.radioSelected : {})
                }}>
                  {selectedPackage === key && <div style={styles.radioInner} />}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Checkout Button */}
        <button onClick={handleCheckout} style={styles.checkoutButton}>
          Checkout for ${packages[selectedPackage].price}
        </button>

        {/* Footer Note */}
        <div style={styles.footer}>
          One-time purchase • Points never expire • Secure payment via Stripe
        </div>
      </div>
    </div>,
    document.body
  );
};

const getStyles = (isLightMode) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    maxWidth: '900px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
    border: '1px solid #e5e7eb',
  },
  header: {
    padding: '24px 32px 20px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #f0f0f0',
  },
  headerText: {
    flex: 1,
    textAlign: 'left',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: '700',
    margin: 0,
    marginBottom: '5px',
    color: '#111',
    letterSpacing: '-0.01em',
    textAlign: 'left',
  },
  subtitle: {
    fontSize: '0.9rem',
    fontWeight: '400',
    margin: 0,
    color: '#6b7280',
    lineHeight: '1.4',
    textAlign: 'left',
  },
  closeButton: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    color: '#6b7280',
    fontSize: '18px',
    cursor: 'pointer',
    padding: 0,
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '9px',
    transition: 'all 0.12s',
  },
  packagesContainer: {
    padding: '28px 32px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  packageCard: {
    position: 'relative',
    border: '1px solid #e5e7eb',
    borderRadius: '14px',
    padding: '26px 22px',
    cursor: 'pointer',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '260px',
  },
  packageCardSelected: {
    border: '2px solid #111',
    backgroundColor: '#ffffff',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },
  badge: {
    position: 'absolute',
    top: '-10px',
    left: '16px',
    backgroundColor: '#111',
    color: '#ffffff',
    fontSize: '0.65rem',
    fontWeight: '700',
    padding: '5px 10px',
    borderRadius: '6px',
    letterSpacing: '0.06em',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  packageHeader: {
    textAlign: 'center',
    marginBottom: '18px',
    paddingTop: '6px',
  },
  pointsAmount: {
    fontSize: '2.3rem',
    fontWeight: '800',
    color: '#111',
    letterSpacing: '-0.02em',
    marginBottom: '6px',
    lineHeight: '1',
  },
  pointsLabel: {
    fontSize: '0.72rem',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginTop: '4px',
  },
  price: {
    fontSize: '2.6rem',
    fontWeight: '800',
    color: '#111',
    marginTop: '10px',
    lineHeight: '1',
    textAlign: 'center',
    letterSpacing: '-0.02em',
  },
  description: {
    color: '#6b7280',
    fontSize: '0.875rem',
    marginTop: '10px',
    marginBottom: '14px',
    lineHeight: '1.5',
    textAlign: 'center',
    flex: 1,
  },
  pricePerPoint: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    marginBottom: '14px',
    textAlign: 'center',
    fontWeight: '500',
  },
  tierBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginBottom: '14px',
    padding: '11px 12px',
    backgroundColor: '#fafafa',
    borderRadius: '9px',
    border: '1px solid #f0f0f0',
  },
  tierItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.78rem',
    color: '#6b7280',
  },
  tierEmoji: {
    marginRight: '6px',
  },
  tierCount: {
    fontWeight: '600',
    color: '#111',
  },
  radioContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 'auto',
  },
  radio: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  radioSelected: {
    border: '2px solid #111',
    backgroundColor: '#111',
    boxShadow: '0 0 0 4px rgba(0, 0, 0, 0.08)',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
  },
  checkoutButton: {
    margin: '0 32px 24px 32px',
    width: 'calc(100% - 64px)',
    backgroundColor: '#111',
    color: '#ffffff',
    border: '1px solid #111',
    borderRadius: '12px',
    padding: '15px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.12s',
    letterSpacing: '-0.005em',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  footer: {
    textAlign: 'center',
    padding: '0 32px 24px 32px',
    color: '#9ca3af',
    fontSize: '0.78rem',
    lineHeight: '1.6',
  },
});

export default PointsPurchaseModal;